<template>
  <div
    v-bind="$attrs"
    v-on="$listeners"
    v-click-outside="hideDropdown"
    :style="style.container"
  >
    <slot />
    <div
      ref="selectionContainer"
      tabindex="0"
      :disabled="disabled"
      :show="show"
      @click="switchDropdown"
      @keydown="navigateOption"
      :class="$style.selectionContainer"
      :style="style.selectionContainer"
    >
      <div
        :class="$style.expandSymbol"
        :style="style.expandSymbol"
      >
        <slot name="expand-symbol-left" />
      </div>
      <div
        v-if="selectedIndex == placeholderIndex"
        :class="$style.placeholder"
        :style="style.placeholder"
      >
        <slot name="placeholder" />
      </div>
      <div
        v-else
        :class="$style.selection"
        :style="style.selection"
      >
        <slot name="selection">
          {{ this.options[selectedIndex][labelKey] }}
        </slot>
      </div>
      <div
        :class="$style.expandSymbol"
        :style="style.expandSymbol"
      >
        <slot name="expand-symbol-right" />
      </div>
      <div
        v-if="!disabled && show"
        :disabled="disabled"
        :class="$style.dropdown"
        :style="style.dropdown"
      >
        <div ref="dropdownTopWrapper">
          <slot name="dropdown-top" />
        </div>
        <div
          ref="dropdownList"
          :style="style.dropdownList"
        >
          <div
            v-for="(option, index) in options"
            :key="index"
            :disabled="optionDisabled(index)"
            :selected="optionSelected(index)"
            @click="selectOption(index)"
            :class="$style.row"
            :style="style.row"
          >
            <div :style="style.selectSymbol">
              <slot
                name="select-symbol"
                :disabled="optionDisabled(index)"
                :index="index"
                :label="option[labelKey]"
                :option="option"
                :selected="optionSelected(index)"
              />
            </div>
            <div :style="style.option">
              <slot
                name="option"
                :disabled="optionDisabled(index)"
                :index="index"
                :label="option[labelKey]"
                :option="option"
                :selected="optionSelected(index)"
              >
                {{ option[labelKey] }}
              </slot>
            </div>
            <div :style="style.actionSymbol">
              <slot
                name="action-symbol"
                :disabled="optionDisabled(index)"
                :index="index"
                :label="option[labelKey]"
                :option="option"
                :selected="optionSelected(index)"
              />
            </div>
          </div>
        </div>
        <slot name="dropdown-bottom" />
      </div>
    </div>
  </div>
</template>

<script>
  import ClickOutside from 'vue-click-outside'
  import { CssStyleProcessor } from '@js/vue-style-utils'
  import { KeyError } from '@js/exceptions'
  import { isArrowDown, isArrowUp, isEnter } from '@js/event-utils'

  export default {
    name: 'BaseDropdown',
    inheritAttrs: false,
    model: {
      prop: 'vmodelValue',
      event: 'change',
    },
    directives: {
      ClickOutside,
    },
    props: {
      vmodelValue: {
        type: [Number, Object, String],
        required: false,
        default: null,
      },
      autoScroll: {
        type: Boolean,
        required: false,
        default: false,
      },
      autoScrollOffset: {
        type: [Number, String],
        required: false,
        default: 0,
      },
      disabled: {
        type: Boolean,
        required: false,
        default: false,
      },
      idKey: {
        type: String,
        required: false,
        default: 'id',
      },
      labelKey: {
        type: String,
        required: false,
        default: 'label',
      },
      options: {
        type: Array,
        required: false,
        default: () => [],
      },
      optionSelectedFunction: {
        type: [Function, Object],
        required: false,
        default: null,
      },
      placeholderIndex: {
        type: [Boolean, Object],
        required: false,
        default: null,
      },
      styleConfig: {
        type: Object,
        required: false,
        default: () => {},
      },
      zIndexDropdown: {
        type: Number,
        required: false,
        default: 10,
      },
    },
    data: function () {
      return {
        selectedIndex: this.getSelectedIndex(this.vmodelValue),
        show: false,
        style: {
          actionSymbol: {},
          container: {
            zIndex: this.zIndexDropdown,
          },
          dropdown: {
            left: 0,
            position: 'absolute',
            width: '100%',
            zIndex: this.zIndexDropdown,
          },
          dropdownList: {
            overflow: 'scroll',
            width: '100%',
          },
          expandSymbol: {},
          option: {
            flex: 1,
          },
          placeholder: {
            flex: 1,
          },
          row: {
            alignItems: 'center',
            display: 'flex',
            width: '100%',
          },
          selectionContainer: {
            alignItems: 'center',
            display: 'flex',
            position: 'relative',
            outline: 'none',
            width: '100%',
          },
          selection: {
            flex: 1,
          },
          selectSymbol: {},
        },
      };
    },
    created: function () {
      const proc = new CssStyleProcessor(
        '--v2-base-dropdown',
        ['selected', 'show', 'selected-hover'],
      );

      Object.assign(
        this.style.actionSymbol,
        this.styleConfig.actionSymbol,
      );
      Object.assign(
        this.style.container,
        this.styleConfig.container,
      );
      Object.assign(
        this.style.dropdown,
        proc.getExtendedStyle(this.styleConfig.dropdown, 'dropdown'),
      );
      Object.assign(
        this.style.dropdownList,
        this.styleConfig.dropdownList,
      );
      Object.assign(
        this.style.expandSymbol,
        proc.getExtendedStyle(this.styleConfig.expandSymbol, 'expandSymbol'),
      );
      Object.assign(
        this.style.option,
        this.styleConfig.option,
      );
      Object.assign(
        this.style.placeholder,
        proc.getExtendedStyle(this.styleConfig.placeholder, 'placeholder'),
      );
      Object.assign(
        this.style.row,
        proc.getExtendedStyle(this.styleConfig.row, 'row'),
      );
      Object.assign(
        this.style.selectionContainer,
        proc.getExtendedStyle(
          this.styleConfig.selectionContainer,
          'selectionContainer'),
      );
      Object.assign(
        this.style.selection,
        proc.getExtendedStyle(this.styleConfig.selection, 'selection'),
      );
      Object.assign(
        this.style.selectSymbol,
        this.styleConfig.selectSymbol,
      );
    },
    mounted: function () {
      Object.assign(
        this.style.dropdown,
        {
          top: `${this.$refs.selectionContainer.offsetHeight}px`,
        },
      );
    },
    computed: {
      selectedOptionLabel: function () {
        return (
          (this.selectedIndex === null ||
           this.selectedIndex === this.placeholderIndex)
            ? this.placeholder
            : this.options[this.selectedIndex][this.labelKey]
        );
      },
    },
    methods: {
      getSelectedIndex: function (vmodelValue) {
        this.validateIdKey();
        const index = this.options
          .findIndex(elem => elem[this.idKey] === vmodelValue);
        return index < 0 ? null : index;
      },
      hideDropdown: function () {
        this.show = false;
      },
      navigateOption: function (event) {
        if (
          this.disabled ||
          (!isArrowDown(event) && (!isArrowUp(event)) && (!isEnter(event)))
        ) {
          return;
        }

        if (isArrowDown(event)) {
          this.selectNextOption();
        } else if (isArrowUp(event)) {
          this.selectPreviousOption();
        } else if (isEnter(event)) {
          this.switchDropdown();
        }
      },
      optionDisabled: function (index) {
        return this.options[index] ? (this.options[index].disabled ? true : false) : true;
      },
      optionSelected: function (index) {
        if (this.optionSelectedFunction) {
          return this.optionSelectedFunction(index);
        }

        return index !== this.placeholderIndex
           ? index === this.selectedIndex : false;
      },
      scrollToSelectedOption () {
        if (
          !this.autoScroll ||
          !this.show ||
          this.selectedIndex === null ||
          this.selectedIndex === this.placeholderIndex
        ) {
          return;
        }

        const selectedOption = this.$refs.dropdownList.children[this.selectedIndex];
        this.$refs.dropdownList.scrollTop = (
          selectedOption.offsetTop
          + parseInt(this.autoScrollOffset)
          - this.$refs.dropdownTopWrapper.clientHeight
        );
      },
      selectNextOption: function () {
        if (this.options.length === 0) {
          this.selectedIndex = this.placeholderIndex;
        } else if (this.selectedIndex === this.placeholderIndex) {
          this.selectedIndex = 0;
        } else if (this.selectedIndex >= this.options.length - 1) {
          this.selectedIndex = this.options.length - 1;
        } else {
          this.selectedIndex++;
        }
      },
      selectOption: function (index) {
        if (!this.disabled) {
          this.selectedIndex = index;
          this.$emit('select-option', index);

          if (
            this.options[this.selectedIndex].hookFunction !== undefined &&
            this.options[this.selectedIndex].hookFunction !== null
          ) {
            this.options[this.selectedIndex].hookFunction();
          }
        }
      },
      selectPreviousOption: function () {
        if (this.options.length === 0) {
          this.selectedIndex = this.placeholderIndex;
        } else if (this.selectedIndex === this.placeholderIndex) {
          return;
        } else if (this.selectedIndex <= 0) {
          this.selectedIndex = 0;
        } else {
          this.selectedIndex--;
        }
      },
      switchDropdown: function () {
        if (!this.disabled) {
          this.show = !this.show;
        }
      },
      validateIdKey: function () {
        if (!this.options.every(obj => this.idKey in obj)) {
          throw new KeyError('The specified idKey not found in given options.');
        }
      },
    },
    watch: {
      vmodelValue: function (newVal, oldVal) {
        this.selectedIndex = this.getSelectedIndex(newVal);
      },
      idKey: function (newVal, oldVal) {
        this.validateIdKey();
      },
      selectedIndex: function (newVal, oldVal) {
        const selectedOptionId = newVal === null
          ? null
          : this.options[newVal][this.idKey];

        this.$emit('change', selectedOptionId);
        this.$nextTick(() => {
          this.scrollToSelectedOption();
        });
      },
      show: function (newVal, oldVal) {
        if(this.selectedIndex === this.placeholderIndex) {
          return;
        }

        this.$nextTick(() => {
          this.scrollToSelectedOption();
        });
      },
    },
  };
</script>

<style lang="stylus" module>
  .dropdown
    background-color var(--v2-base-dropdown-dropdown-background-color)
    border var(--v2-base-dropdown-dropdown-border)
    border-radius var(--v2-base-dropdown-dropdown-border-radius)
    border-width var(--v2-base-dropdown-dropdown-border-width)
    box-shadow var(--v2-base-dropdown-dropdown-box-shadow)
    cursor var(--v2-base-dropdown-dropdown-cursor)
    &[selected]
      background-color var(--v2-base-dropdown-dropdown-background-color-selected)
      border var(--v2-base-dropdown-dropdown-border-selected)
      border-radius var(--v2-base-dropdown-dropdown-border-radius-selected)
      border-width var(--v2-base-dropdown-dropdown-border-width-selected)
      box-shadow var(--v2-base-dropdown-dropdown-box-shadow-selected)
      cursor var(--v2-base-dropdown-dropdown-cursor-selected)
    &:hover
      background-color var(--v2-base-dropdown-dropdown-background-color-hover)
      border var(--v2-base-dropdown-dropdown-border-hover)
      border-radius var(--v2-base-dropdown-dropdown-border-radius-hover)
      border-width var(--v2-base-dropdown-dropdown-border-width-hover)
      box-shadow var(--v2-base-dropdown-dropdown-box-shadow-hover)
      cursor var(--v2-base-dropdown-dropdown-cursor-hover)
    &:active
      background-color var(--v2-base-dropdown-dropdown-background-color-active)
      border var(--v2-base-dropdown-dropdown-border-active)
      border-radius var(--v2-base-dropdown-dropdown-border-radius-active)
      border-width var(--v2-base-dropdown-dropdown-border-width-active)
      box-shadow var(--v2-base-dropdown-dropdown-box-shadow-active)
      cursor var(--v2-base-dropdown-dropdown-cursor-active)
    &[disabled]
      background-color var(--v2-base-dropdown-dropdown-background-color-disabled)
      border var(--v2-base-dropdown-dropdown-border-disabled)
      border-radius var(--v2-base-dropdown-dropdown-border-radius-disabled)
      border-width var(--v2-base-dropdown-dropdown-border-width-disabled)
      box-shadow var(--v2-base-dropdown-dropdown-box-shadow-disabled)
      cursor var(--v2-base-dropdown-dropdown-cursor-disabled)

  .expandSymbol
    background-color var(--v2-base-dropdown-expand-symbol-background-color)
    border var(--v2-base-dropdown-expand-symbol-border)
    border-radius var(--v2-base-dropdown-expand-symbol-border-radius)
    border-width var(--v2-base-dropdown-expand-symbol-border-width)
    box-shadow var(--v2-base-dropdown-expand-symbol-box-shadow)
    color var(--v2-base-dropdown-expand-symbol-color)
    font-family var(--v2-base-dropdown-expand-symbol-font-family)
    font-size var(--v2-base-dropdown-expand-symbol-font-size)
    font-weight var(--v2-base-dropdown-expand-symbol-font-weight)
    &[selected]
      background-color var(--v2-base-dropdown-expand-symbol-background-color-selected)
      border var(--v2-base-dropdown-expand-symbol-border-selected)
      border-radius var(--v2-base-dropdown-expand-symbol-border-radius-selected)
      border-width var(--v2-base-dropdown-expand-symbol-border-width-selected)
      box-shadow var(--v2-base-dropdown-expand-symbol-box-shadow-selected)
      color var(--v2-base-dropdown-expand-symbol-color-selected)
      font-family var(--v2-base-dropdown-expand-symbol-font-family-selected)
      font-size var(--v2-base-dropdown-expand-symbol-font-size-selected)
      font-weight var(--v2-base-dropdown-expand-symbol-font-weight-selected)
    &:hover
      background-color var(--v2-base-dropdown-expand-symbol-background-color-hover)
      border var(--v2-base-dropdown-expand-symbol-border-hover)
      border-radius var(--v2-base-dropdown-expand-symbol-border-radius-hover)
      border-width var(--v2-base-dropdown-expand-symbol-border-width-hover)
      box-shadow var(--v2-base-dropdown-expand-symbol-box-shadow-hover)
      color var(--v2-base-dropdown-expand-symbol-color-hover)
      font-family var(--v2-base-dropdown-expand-symbol-font-family-hover)
      font-size var(--v2-base-dropdown-expand-symbol-font-size-hover)
      font-weight var(--v2-base-dropdown-expand-symbol-font-weight-hover)
    &:active
      background-color var(--v2-base-dropdown-expand-symbol-background-color-active)
      border var(--v2-base-dropdown-expand-symbol-border-active)
      border-radius var(--v2-base-dropdown-expand-symbol-border-radius-active)
      border-width var(--v2-base-dropdown-expand-symbol-border-width-active)
      box-shadow var(--v2-base-dropdown-expand-symbol-box-shadow-active)
      color var(--v2-base-dropdown-expand-symbol-color-active)
      font-family var(--v2-base-dropdown-expand-symbol-font-family-active)
      font-size var(--v2-base-dropdown-expand-symbol-font-size-active)
      font-weight var(--v2-base-dropdown-expand-symbol-font-weight-active)
    &[disabled]
      background-color var(--v2-base-dropdown-expand-symbol-background-color-disabled)
      border var(--v2-base-dropdown-expand-symbol-border-disabled)
      border-radius var(--v2-base-dropdown-expand-symbol-border-radius-disabled)
      border-width var(--v2-base-dropdown-expand-symbol-border-width-disabled)
      box-shadow var(--v2-base-dropdown-expand-symbol-box-shadow-disabled)
      color var(--v2-base-dropdown-expand-symbol-color-disabled)
      font-family var(--v2-base-dropdown-expand-symbol-font-family-disabled)
      font-size var(--v2-base-dropdown-expand-symbol-font-size-disabled)
      font-weight var(--v2-base-dropdown-expand-symbol-font-weight-disabled)

  .placeholder
    color var(--v2-base-dropdown-placeholder-color)
    font-family var(--v2-base-dropdown-placeholder-font-family)
    font-size var(--v2-base-dropdown-placeholder-font-size)
    font-weight var(--v2-base-dropdown-placeholder-font-weight)
    &[selected]
      color var(--v2-base-dropdown-placeholder-color-selected)
      font-family var(--v2-base-dropdown-placeholder-font-family-selected)
      font-size var(--v2-base-dropdown-placeholder-font-size-selected)
      font-weight var(--v2-base-dropdown-placeholder-font-weight-selected)
    &:hover
      color var(--v2-base-dropdown-placeholder-color-hover)
      font-family var(--v2-base-dropdown-placeholder-font-family-hover)
      font-size var(--v2-base-dropdown-placeholder-font-size-hover)
      font-weight var(--v2-base-dropdown-placeholder-font-weight-hover)
    &:active
      color var(--v2-base-dropdown-placeholder-color-active)
      font-family var(--v2-base-dropdown-placeholder-font-family-active)
      font-size var(--v2-base-dropdown-placeholder-font-size-active)
      font-weight var(--v2-base-dropdown-placeholder-font-weight-active)
    &[disabled]
      color var(--v2-base-dropdown-placeholder-color-disabled)
      font-family var(--v2-base-dropdown-placeholder-font-family-disabled)
      font-size var(--v2-base-dropdown-placeholder-font-size-disabled)
      font-weight var(--v2-base-dropdown-placeholder-font-weight-disabled)

  .row
    background-color var(--v2-base-dropdown-row-background-color)
    border var(--v2-base-dropdown-row-border)
    border-radius var(--v2-base-dropdown-row-border-radius)
    border-width var(--v2-base-dropdown-row-border-width)
    box-shadow var(--v2-base-dropdown-row-box-shadow)
    color var(--v2-base-dropdown-row-color)
    cursor var(--v2-base-dropdown-row-cursor)
    font-family var(--v2-base-dropdown-row-font-family)
    font-size var(--v2-base-dropdown-row-font-size)
    font-weight var(--v2-base-dropdown-row-font-weight)
    &[selected]
      background-color var(--v2-base-dropdown-row-background-color-selected)
      border var(--v2-base-dropdown-row-border-selected)
      border-radius var(--v2-base-dropdown-row-border-radius-selected)
      border-width var(--v2-base-dropdown-row-border-width-selected)
      box-shadow var(--v2-base-dropdown-row-box-shadow-selected)
      color var(--v2-base-dropdown-row-color-selected)
      cursor var(--v2-base-dropdown-row-cursor-selected)
      font-family var(--v2-base-dropdown-row-font-family-selected)
      font-size var(--v2-base-dropdown-row-font-size-selected)
      font-weight var(--v2-base-dropdown-row-font-weight-selected)
    &:hover
      background-color var(--v2-base-dropdown-row-background-color-hover)
      border var(--v2-base-dropdown-row-border-hover)
      border-radius var(--v2-base-dropdown-row-border-radius-hover)
      border-width var(--v2-base-dropdown-row-border-width-hover)
      box-shadow var(--v2-base-dropdown-row-box-shadow-hover)
      color var(--v2-base-dropdown-row-color-hover)
      cursor var(--v2-base-dropdown-row-cursor-hover)
      font-family var(--v2-base-dropdown-row-font-family-hover)
      font-size var(--v2-base-dropdown-row-font-size-hover)
      font-weight var(--v2-base-dropdown-row-font-weight-hover)
    &:active
      background-color var(--v2-base-dropdown-row-background-color-active)
      border var(--v2-base-dropdown-row-border-active)
      border-radius var(--v2-base-dropdown-row-border-radius-active)
      border-width var(--v2-base-dropdown-row-border-width-active)
      box-shadow var(--v2-base-dropdown-row-box-shadow-active)
      color var(--v2-base-dropdown-row-color-active)
      cursor var(--v2-base-dropdown-row-cursor-active)
      font-family var(--v2-base-dropdown-row-font-family-active)
      font-size var(--v2-base-dropdown-row-font-size-active)
      font-weight var(--v2-base-dropdown-row-font-weight-active)
    &[disabled]
      background-color var(--v2-base-dropdown-row-background-color-disabled)
      border var(--v2-base-dropdown-row-border-disabled)
      border-radius var(--v2-base-dropdown-row-border-radius-disabled)
      border-width var(--v2-base-dropdown-row-border-width-disabled)
      box-shadow var(--v2-base-dropdown-row-box-shadow-disabled)
      color var(--v2-base-dropdown-row-color-disabled)
      cursor var(--v2-base-dropdown-row-cursor-disabled)
      font-family var(--v2-base-dropdown-row-font-family-disabled)
      font-size var(--v2-base-dropdown-row-font-size-disabled)
      font-weight var(--v2-base-dropdown-row-font-weight-disabled)

  .selection
    color var(--v2-base-dropdown-selection-color)
    font-family var(--v2-base-dropdown-selection-font-family)
    font-size var(--v2-base-dropdown-selection-font-size)
    font-weight var(--v2-base-dropdown-selection-font-weight)
    &[selected]
      color var(--v2-base-dropdown-selection-color-selected)
      font-family var(--v2-base-dropdown-selection-font-family-selected)
      font-size var(--v2-base-dropdown-selection-font-size-selected)
      font-weight var(--v2-base-dropdown-selection-font-weight-selected)
    &:hover
      color var(--v2-base-dropdown-selection-color-hover)
      font-family var(--v2-base-dropdown-selection-font-family-hover)
      font-size var(--v2-base-dropdown-selection-font-size-hover)
      font-weight var(--v2-base-dropdown-selection-font-weight-hover)
    &:active
      color var(--v2-base-dropdown-selection-color-active)
      font-family var(--v2-base-dropdown-selection-font-family-active)
      font-size var(--v2-base-dropdown-selection-font-size-active)
      font-weight var(--v2-base-dropdown-selection-font-weight-active)
    &[disabled]
      color var(--v2-base-dropdown-selection-color-disabled)
      font-family var(--v2-base-dropdown-selection-font-family-disabled)
      font-size var(--v2-base-dropdown-selection-font-size-disabled)
      font-weight var(--v2-base-dropdown-selection-font-weight-disabled)

  .selectionContainer
    background-color var(--v2-base-dropdown-selection-container-background-color)
    border var(--v2-base-dropdown-selection-container-border)
    border-radius var(--v2-base-dropdown-selection-container-border-radius)
    border-width var(--v2-base-dropdown-selection-container-border-width)
    box-shadow var(--v2-base-dropdown-selection-container-box-shadow)
    cursor var(--v2-base-dropdown-selection-container-cursor)
    &[selected]
      background-color var(--v2-base-dropdown-selection-container-background-color-selected)
      border var(--v2-base-dropdown-selection-container-border-selected)
      border-radius var(--v2-base-dropdown-selection-container-border-radius-selected)
      border-width var(--v2-base-dropdown-selection-container-border-width-selected)
      box-shadow var(--v2-base-dropdown-selection-container-box-shadow-selected)
      cursor var(--v2-base-dropdown-selection-container-cursor-selected)
    &:hover
      background-color var(--v2-base-dropdown-selection-container-background-color-hover)
      border var(--v2-base-dropdown-selection-container-border-hover)
      border-radius var(--v2-base-dropdown-selection-container-border-radius-hover)
      border-width var(--v2-base-dropdown-selection-container-border-width-hover)
      box-shadow var(--v2-base-dropdown-selection-container-box-shadow-hover)
      cursor var(--v2-base-dropdown-selection-container-cursor-hover)
    &:active
      background-color var(--v2-base-dropdown-selection-container-background-color-active)
      border var(--v2-base-dropdown-selection-container-border-active)
      border-radius var(--v2-base-dropdown-selection-container-border-radius-active)
      border-width var(--v2-base-dropdown-selection-container-border-width-active)
      box-shadow var(--v2-base-dropdown-selection-container-box-shadow-active)
      cursor var(--v2-base-dropdown-selection-container-cursor-active)
    &[show]
      background-color var(--v2-base-dropdown-selection-container-background-color-show)
      border var(--v2-base-dropdown-selection-container-border-show)
      border-radius var(--v2-base-dropdown-selection-container-border-radius-show)
      border-width var(--v2-base-dropdown-selection-container-border-width-show)
      box-shadow var(--v2-base-dropdown-selection-container-box-shadow-show)
      cursor var(--v2-base-dropdown-selection-container-cursor-show)
    &[disabled]
      background-color var(--v2-base-dropdown-selection-container-background-color-disabled)
      border var(--v2-base-dropdown-selection-container-border-disabled)
      border-radius var(--v2-base-dropdown-selection-container-border-radius-disabled)
      border-width var(--v2-base-dropdown-selection-container-border-width-disabled)
      box-shadow var(--v2-base-dropdown-selection-container-box-shadow-disabled)
      cursor var(--v2-base-dropdown-selection-container-cursor-disabled)
</style>
